import * as React from 'react';
import { useEffect, useContext } from 'react';
import * as TFF from '@tff/types/tff';
import { LoadingContext } from '../contexts/LoadingContextProvider';
import {
  MidocoBookingInfo,
  MidocoBookingInfoAttributeValue,
  IMidocoSearchOrderCriteria,
  OrderServiceLambdaOutput,
} from '@tff/types/Midoco';
import { useQueryClient } from 'react-query';
import { AxiosResponse } from 'axios';
import { apigeeEndpoint } from '../apis';

export type UseMidocoProps = {
  recordLocator?: string;
  /**
   * OrderNumber this number is known by midoco workers
   */
  orderNo?: number;

  midocoSearchOrderCriteria?: IMidocoSearchOrderCriteria;
};

export type TypeOfStatus =
  | 'FETCHING_MIDOCO_ORDER'
  | 'MIDOCO_ORDER_FETCHED'
  | 'MIDOCO_ORDER_FETCHED_LIST'
  | 'MIDOCO_ORDER_FAILURE';

export type UseMidocoResponse = {
  midocoFetchedOrderList?: MidocoBookingInfoAttributeValue[];
  midocoFetchedOrder?: TFF.FlightDetails;
  midocoStatus?: TypeOfStatus;
  midocoError?: any;

  midocoWarning?: any;
  resetMidocoState: () => void;
  midocoLoading?: boolean;
};

//
export const useMidoco = (midocoProps?: UseMidocoProps): UseMidocoResponse => {
  const [midocoStatus, setMidocoStatus] = React.useState<TypeOfStatus>();
  const [midocoFetchedOrder, setMidocoFetchedOrder] = React.useState<TFF.FlightDetails>();
  const [midocoFetchedOrderList, setMidocoFetchedOrderList] = React.useState<MidocoBookingInfoAttributeValue[]>();
  const [midocoError, setMidocoError] = React.useState<any>();
  const [midocoWarning, setMidocoWarning] = React.useState<any>();
  const [midocoLoading, setMidocoLoading] = React.useState<boolean>(false);

  const { showLoading, closeLoading } = useContext(LoadingContext);
  const queryClient = useQueryClient();

  const resetMidocoState = (): void => {
    setMidocoFetchedOrder(undefined);
    setMidocoFetchedOrderList(undefined);
    setMidocoError(undefined);
    setMidocoWarning(undefined);
    setMidocoStatus(undefined);
  };

  useEffect(() => {
    const fetchOrderFromMidoco = async (midocoProps: UseMidocoProps): Promise<void> => {
      try {
        setMidocoStatus('FETCHING_MIDOCO_ORDER');
        setMidocoLoading(true);
        let midocoOrderNo = midocoProps.orderNo;
        let midocoBookingInfoResponse: MidocoBookingInfo | MidocoBookingInfo[] | undefined;

        // if recordLocator is given, we have to search first by recordLocator and source to find out the midoco orderNumber,
        if (!midocoOrderNo && (midocoProps.recordLocator || midocoProps.midocoSearchOrderCriteria)) {
          let midocoSearchOrderCriteria: IMidocoSearchOrderCriteria | undefined = midocoProps.midocoSearchOrderCriteria;

          if (midocoProps.recordLocator) {
            showLoading(`Loading Midoco Order for RecordLocator: ${midocoProps.recordLocator}`);
            midocoSearchOrderCriteria = {
              idType: 'bookingID',
              crsBookingId: `${midocoProps.recordLocator}`,
            };
          } else {
            showLoading(`Search Midoco Orders`);
          }

          const midocoSearchResponse = await apigeeEndpoint.post<OrderServiceLambdaOutput>(`/midoco/order/search`, {
            maxReturned: 30,
            MidocoSearchOrderCriteria: {
              ...midocoSearchOrderCriteria,
              orgunitName: window.ENV?.TFF_MIDOCO_ORGUNIT_NAME,
            },
          });

          midocoBookingInfoResponse = midocoSearchResponse?.data?.body?.MidocoBookingInfo;

          if (!midocoBookingInfoResponse) {
            setMidocoStatus('MIDOCO_ORDER_FAILURE');
            setMidocoError('MIDOCO_SEARCH_RETURNS_NO_MATCH');
            return;
          }

          if (Array.isArray(midocoBookingInfoResponse)) {
            setMidocoStatus('MIDOCO_ORDER_FETCHED_LIST');
            setMidocoFetchedOrderList(midocoBookingInfoResponse.map(mbi => mbi.attributes));
            return;
          } else {
            midocoOrderNo = Number(midocoBookingInfoResponse?.attributes.orderNo);
          }
        }

        if (!midocoOrderNo) {
          setMidocoStatus('MIDOCO_ORDER_FAILURE');
          setMidocoError('MIDOCO_ORDER_NUMBER_NOT_FOUND');
        } else {
          showLoading(`Loading Midoco Order for OrderNumber: ${midocoOrderNo}`);

          const response: AxiosResponse<TFF.RetrieveFlightResponse> = await queryClient.fetchQuery(
            `midocoOrderNo::${midocoOrderNo}`,
            async () => {
              const response = await apigeeEndpoint
                .get<TFF.RetrieveFlightResponse>(`/midoco/order/orderno/${midocoOrderNo}`)
                .catch(e => {
                  return undefined;
                });
              return response;
            },
            {
              retry: 2,
              // staleTime: 3000,
              cacheTime: 10000,
              initialData: () => {
                return queryClient.getQueryData(`midocoOrderNo::${midocoOrderNo}`);
              },
            },
          );

          if (!response) {
            setMidocoStatus('MIDOCO_ORDER_FAILURE');
            setMidocoError('MIDOCO_ORDER_NOT_FOUND');
            return;
          }
          const retrieveResponse = response?.data;

          if (retrieveResponse?.Status === 'SUCCESS') {
            setMidocoStatus('MIDOCO_ORDER_FETCHED');
            setMidocoFetchedOrder(retrieveResponse.Booking);
          } else {
            setMidocoStatus('MIDOCO_ORDER_FAILURE');
            setMidocoError(retrieveResponse?.Error || 'MIDOCO_ORDER_RETRIEVAL_FAILED');
            return;
          }

          // retrieveResponse?.Warning && setMidocoWarning(retrieveResponse?.Warning);
        }
      } catch (error) {
        setMidocoError(error);
        setMidocoStatus('MIDOCO_ORDER_FAILURE');
        setMidocoLoading(false);
      } finally {
        setMidocoLoading(false);
        closeLoading();
      }
    };

    if (
      (midocoProps?.orderNo || midocoProps?.recordLocator || midocoProps?.midocoSearchOrderCriteria) &&
      !midocoStatus
    ) {
      void fetchOrderFromMidoco(midocoProps);
    }
  }, [midocoProps, midocoStatus]);

  return {
    midocoError,
    midocoWarning,
    midocoFetchedOrder,
    midocoFetchedOrderList,
    midocoStatus,
    resetMidocoState,
    midocoLoading,
  };
};
